import { useCallback, useEffect, useState } from "react";
import { useKohde } from "./Kohdekonteksti";
import {
  api, ApiError, type Kayttaja, type Kohde, type KalustoLaji, type KalustoQr as QrTila,
  type KalustoQrEhdokas, type KalustoQrKorvausSyy, type KalustoSijaintilaji, type KalustoTyyppi,
} from "./api";
import { nayttonimi, vaihtoehdot } from "./nayttonimi";
import { naytaPvm } from "./pvm";
import { Tilasana, type Tilalaji } from "./Tila";
import { sijaintiTeksti } from "./Kalusto";
import { Skannaus } from "./Skannaus";
import { t, virheTeksti } from "./kieli";

/* ═══════════════════════════════════════════════════════════
   QR-tarran näkymä (172; Toni ja Markku 25.9., Toni 26.9. K2 a · K3 b).

   Tarran QR on osoite sampo.pro/k/<asiakas>/<n> → instanssin /k/<n> →
   tämä näkymä. Kirjautumaton näkee kirjautumissivun ja palaa tänne,
   koska osoite on hash-osassa (K2 a). Numero on TARRAN oma, ei laitteen.

   Kolme tilaa, ja jokaisella oma ruutu:
     sitomaton  lisää laite tällä tarralla TAI sido olemassa olevaan
                (yksi työnkulku: tarran liimaaja on se joka tietää)
     sidottu    laitteen kortti ja toiminnot oikeuksien mukaan; puuttuva
                toiminto sanotaan syyn kanssa, ei harmaata nappia
     korvattu   ei avaa laitetta (K3 b) — kertoo milloin ja miksi
   Näkymä on puhelinta varten: yksi palsta, isot napit, iso numero.
   ═══════════════════════════════════════════════════════════ */

const TILA_LAJI: Record<string, Tilalaji> = {
  voimassa: "kunnossa", vanhenee: "varoitus", vanhentunut: "vika", pois_kaytosta: "vika", ei_katsastusta: "ei_mitattu",
};

export default function KalustoQr({ kayttaja, numero }: { kayttaja: Kayttaja; numero: string | null }) {
  const n = numero && /^[1-9][0-9]{0,8}$/.test(numero) ? Number(numero) : null;
  const [tila, setTila] = useState<QrTila | null>(null);
  const [virhe, setVirhe] = useState<string | null>(null);
  const [ilmoitus, setIlmoitus] = useState<string | null>(null);

  const lataa = useCallback(() => {
    if (n === null) return;
    setVirhe(null);
    api.kalustoQr(n).then(setTila).catch((e) => setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("qr.tietoja_ei_saatu")));
  }, [n]);
  useEffect(lataa, [lataa]);
  const [juuriLisatty, setJuuriLisatty] = useState(false);
  useEffect(() => { setJuuriLisatty(false); }, [n]);

  // Korjaus a (Toni 28.9.2026): valikon «Tarran numero» avaa tämän näkymän ilman numeroa.
  if (numero === null) return <Syotto />;
  if (n === null) {
    return <Runko numero={numero}><p className="qr__esto" data-qr-tila="virheellinen">{t("qr.ei_kelvollista")}</p><Syotto upotettu /></Runko>;
  }
  if (virhe) return <Runko numero={String(n)}><p className="qr__esto" role="alert" data-qr-tila="virhe">{virhe}</p></Runko>;
  if (!tila) return <Runko numero={String(n)}><p className="dim">{t("qr.haetaan")}</p></Runko>;

  const valmis = (viesti: string) => { setIlmoitus(viesti); lataa(); };

  return (
    <Runko numero={String(n)} koodi={tila.koodi}>
      {ilmoitus && <p className="qr__ilmoitus" role="status">{ilmoitus}</p>}
      {tila.tila === "korvattu" && (
        <div data-qr-tila="korvattu" data-sisalto="qr">
          <p className="qr__otsikko">{t("qr.korvattu_otsikko")}</p>
          <p>{t("qr.korvattu")} {naytaPvm(tila.korvattu)} · {nayttonimi("kalusto_qr_korvaus_syy", tila.korvaus_syy)}</p>
          <p className="dim">{tila.korvaus_peruste}</p>
          <p>{t("qr.korvattu_ohje")}</p>
        </div>
      )}
      {/* 181: numero sarjan ulkopuolella on syy, ei virhesivu. Ihminen on lukenut tai naputtanut
          numeron väärin, tai tarra on toisen yrityksen: kerrotaan kumpaa epäillä ja annetaan yrittää uudelleen. */}
      {tila.tila === "ei_sarjassa" && (
        <div data-qr-tila="ei_sarjassa" data-sisalto="qr">
          <p className="qr__otsikko">{t("qr.ei_sarjassa", { n: tila.numero })}</p>
          <p className="qr__esto" data-qr-esto>{tila.viesti}</p>
          <Syotto upotettu />
        </div>
      )}
      {tila.tila === "sidottu" && <Sidottu tila={tila} valmis={valmis} juuriLisatty={juuriLisatty} />}
      {tila.tila === "sitomaton" && <Sitomaton n={n} tila={tila} kayttaja={kayttaja} valmis={valmis} lisatty={() => setJuuriLisatty(true)} />}
    </Runko>
  );
}

function Runko({ numero, koodi, children }: { numero: string; koodi?: string | null; children: React.ReactNode }) {
  return (
    <main className="qr" data-nakyma="k">
      <p className="qr__numero" data-qr-numero={numero}>
        <span className="qr__etuliite">{t("hl.tarra")}{koodi ? ` ${koodi}` : ""}</span> {numero}
      </p>
      {children}
    </main>
  );
}

/* ═══ Tarran numero ilman QR:ää (korjaus a, Toni 28.9.2026) ══
   Mitattu 27.9.: naarmuuntunut QR + luettava «17» ei vienyt kortille millään näppäilyllä — kenttätekijän
   valikossa ei ollut Kalustoa, ja ainoa tie oli kirjoittaa sampo.pro/k/pt/17 selaimeen. Numero oli
   luettava mutta hyödytön, ja yksi naarmu maksoi uuden tarran, uuden sidonnan ja laitteen historian.
   Syöttö EI ole oma näkymänsä vaan osoite #/k/<n>: sidottu avaa kortin, sitomaton lisäyksen —
   täsmälleen sama polku kuin skannauksessa, joten kaksi reittiä samaan paikkaan ei voi erkaantua. */
function Syotto({ upotettu = false }: { upotettu?: boolean }) {
  const [arvo, setArvo] = useState("");
  const [virhe, setVirhe] = useState<string | null>(null);
  const avaa = (e: React.FormEvent) => {
    e.preventDefault();
    const s = arvo.trim();
    if (!/^[1-9][0-9]{0,8}$/.test(s)) { setVirhe(t("qr.kirjoita_numero")); return; }
    window.location.hash = `#/k/${s}`;
  };
  const lomake = (
    <form className="qr__lomake" data-qr-lomake="numero" onSubmit={avaa} noValidate>
      <label>{t("qr.tarran_numero")}
        <input inputMode="numeric" autoComplete="off" value={arvo} autoFocus={!upotettu}
               aria-invalid={virhe ? true : undefined}
               onChange={(e) => { setArvo(e.target.value); setVirhe(null); }} />
      </label>
      {virhe && <p className="qr__esto" role="alert">{virhe}</p>}
      <button type="submit" className="nappi">{t("qr.avaa")}</button>
      {/* 54: kamera syötön vierellä, ei sen tilalla — evätty lupa tai puuttuva kamera jättää käsin kirjoituksen. */}
      <Skannaus loytyi={(n) => { window.location.hash = `#/k/${n}`; }} />
    </form>
  );
  if (upotettu) return lomake;
  return (
    <main className="qr" data-nakyma="k">
      {/* #/ avaisi kenttätekijälle Hubin, jota hän ei näe — Omat tiedot on jokaisen valikossa. */}
      <p><a href="#/minun" data-qr-valikkoon="">‹ {t("qr.valikkoon")}</a></p>
      <p className="qr__otsikko">{t("qr.syotto_otsikko")}</p>
      <p>{t("qr.syotto_ohje")}</p>
      {lomake}
    </main>
  );
}

/** Lomakkeiden sijaintivalinnat: «Hallussa» ei ole valittavissa — haltuun otetaan skannaamalla (173). */
// T439: funktio — nimet käyttäjän kielellä piirtohetkellä.
const sijainnit = () => vaihtoehdot("kalusto_sijainti").filter((v) => v.arvo !== "henkilo");

/* 173 · Toimintovalikko (Toni 26.9.): ota haltuun · palauta · muokkaa · kirjaa tarkastus · katso kortti.
   Kortti on aina näkyvissä (katso kortti). Toiminto jota ei voi tehdä ei ole harmaa nappi vaan lause joka
   nimeää oikeuden ja keneltä sitä pyydetään (kohta 5). Toisen hallussa oleva laite: varoitus ennen
   napautusta ja nappi sanoo keneltä otetaan (kohta 4); ei vahvistusta (kohta 3). */
function Sidottu({ tila, valmis, juuriLisatty = false }: { tila: Extract<QrTila, { tila: "sidottu" }>; valmis: (v: string) => void; juuriLisatty?: boolean }) {
  const l = tila.laite;
  const h = tila.haltija;
  const [korvaus, setKorvaus] = useState(false);
  const [palautus, setPalautus] = useState(false);
  // 76: «Kirjaa sijaintiin» avaa sijaintilomakkeen (haltijattomalle oma reitti, haltijalla palautus).
  const [sijainti, setSijainti] = useState(false);
  const [virhe, setVirhe] = useState<string | null>(null);
  const [peruste, setPeruste] = useState<string | null>(null);
  const ota = async () => {
    setVirhe(null);
    try {
      await api.kalustoQrOta(tila.numero, peruste ?? undefined);
      valmis(h && !h.mina ? t("qr.otit_henkilolta", { nimi: h.nimi ?? "?" }) : t("qr.hallussasi_nyt"));
    } catch (x) {
      if (x instanceof ApiError && x.virhe === "pois_kaytosta_peruste_puuttuu") { setPeruste(""); setVirhe(virheTeksti(x.virhe, x.message)); return; }
      setVirhe(x instanceof ApiError ? virheTeksti(x.virhe, x.message) : t("qr.haltuunotto_virhe"));
    }
  };
  return (
    <div data-qr-tila="sidottu" data-sisalto="qr" data-kalusto={l.id} data-haltija={h ? (h.mina ? "mina" : "toinen") : "ei"}>
      <p className="qr__otsikko">{l.tunnus} · {l.nimi}</p>
      {/* 55: kenttätekijän ehdotus on ehdotus kunnes kalustovastaava päättää; hylätty rivi jää ja sanoo sen. */}
      {l.hyvaksynta === "ehdotettu" && <p className="qr__esto" data-kalusto-hyvaksynta="ehdotettu">{t("qr.ehdotettu")} {naytaPvm(l.ehdotettu)}{l.ehdottaja ? ` · ${l.ehdottaja}` : ""} — {t("qr.odottaa_hyvaksyntaa")}</p>}
      {l.hyvaksynta === "hylatty" && <p className="qr__esto" data-kalusto-hyvaksynta="hylatty">{t("qr.hylatty")} {naytaPvm(l.hyvaksynta_hetki)}{l.hyvaksyja ? ` · ${l.hyvaksyja}` : ""}: {l.hyvaksynta_peruste}</p>}
      {l.poistettu && <p className="qr__esto">{t("qr.poistettu")} {naytaPvm(l.poistettu)} ({nayttonimi("kalusto_poisto_syy", l.poisto_syy)}).</p>}
      {h && (h.mina
        ? <p className="qr__ilmoitus" data-qr-hallussa="mina">{t("qr.hallussasi")}</p>
        : <p className="qr__esto" data-qr-hallussa="toinen">{t("qr.hallussa")}: {h.nimi ?? t("qr.tuntematon_henkilo")}</p>)}
      <dl className="qr__tiedot">
        <dt>{t("pu.tyyppi")}</dt><dd>{[nayttonimi("kalusto_laji", l.laji), l.tyyppi].filter(Boolean).join(" · ")}</dd>
        <dt>{t("qr.sijainti")}</dt><dd>{sijaintiTeksti(l) || <span className="dim">{t("qr.ei_tiedossa")}</span>}</dd>
        <dt>{t("qr.kunto")}</dt><dd>{nayttonimi("kalusto_kunto", l.kunto) || <span className="dim">{t("qr.ei_kirjattu")}</span>}</dd>
        <dt>{t("qr.katsastus")}</dt>
        <dd><Tilasana laji={TILA_LAJI[l.tila] ?? "ei_mitattu"}>
          {nayttonimi("kalusto_tila", l.tila)}{l.katsastus_asti ? ` · ${t("qr.asti", { pvm: naytaPvm(l.katsastus_asti) ?? "" })}` : ""}
        </Tilasana></dd>
        {(l.valmistaja || l.malli) && <><dt>{t("qr.laite")}</dt><dd>{[l.valmistaja, l.malli].filter(Boolean).join(" ")}</dd></>}
        {l.sarjanumero && <><dt>{t("qr.sarjanumero")}</dt><dd>{l.sarjanumero}</dd></>}
        {l.kantavuus && <><dt>{t("qr.kantavuus")}</dt><dd>{l.kantavuus}</dd></>}
      </dl>
      {peruste !== null && (
        <label className="qr__lomake">{t("qr.peruste_siirrolle")}
          <input value={peruste} onChange={(e) => setPeruste(e.target.value)} minLength={3} maxLength={500} /></label>)}
      {/* Päätös 76 (Toni 5.10.2026 klo 11.58): kaksi tasavertaista nappia, ei oletusta eikä isoa kuittausnappia.
          Haltijaton: «Kirjaa sijaintiin» / «Kirjaa itselle». Haltijalla: «Palauta» / «Kirjaa sijaintiin» (= palautus valitulla
          sijainnilla), toisen hallussa: «Ota haltuun henkilöltä X» / «Kirjaa sijaintiin». Lisäyksen jälkeen kortti kysyy;
          jos käyttäjä poistuu valitsematta, laite jää lomakkeen sijaintiin — mitään ei tehdä hänen puolestaan. */}
      {juuriLisatty && !h && (
        <p className="qr__ilmoitus" data-qr-kysymys="sijainti-vai-itselle">{t("qr.lisatty_kysymys", { sijainti: sijaintiTeksti(l) || t("qr.ei_tiedossa") })}</p>
      )}
      <div className="qr__toiminnot qr__toiminnot--pari" data-qr-pari={h ? (h.mina ? "haltija" : "toinen") : "ei-haltijaa"}>
        {tila.oikeudet.sijainti && !sijainti && !palautus && (
          <button className="nappi" data-qr-toiminto="sijainti" onClick={() => { setSijainti(true); setPalautus(false); }}>{t("qr.kirjaa_sijaintiin")}</button>)}
        {tila.oikeudet.ota && (
          <button className="nappi" data-qr-toiminto={h && !h.mina ? "ota" : "itselle"} onClick={ota}>
            {h && !h.mina ? t("qr.ota_henkilolta", { nimi: h.nimi ?? t("qr.tuntematon") }) : t("qr.kirjaa_itselle")}</button>)}
        {tila.oikeudet.palauta && !palautus && !sijainti && (
          <button className="nappi" data-qr-toiminto="palauta" onClick={() => { setPalautus(true); setSijainti(false); }}>{t("qr.palauta")}</button>)}
        {tila.oikeudet.muokkaa && <a className="nappi" href="#/kalusto" data-qr-toiminto="muokkaa">{t("qr.muokkaa")}</a>}
        {tila.oikeudet.tarkastus && <a className="nappi" href="#/kalusto" data-qr-toiminto="tarkastus">{t("qr.kirjaa_tarkastus")}</a>}
        {tila.oikeudet.korvaa && !korvaus && (
          <button className="nappi" data-qr-toiminto="korvaa" onClick={() => setKorvaus(true)}>{t("qr.vaihda_tarra")}</button>)}
      </div>
      {virhe && <p className="qr__esto" role="alert">{virhe}</p>}
      {palautus && <Palautus n={tila.numero} oletus={tila.palautuksen_oletus} peru={() => setPalautus(false)} valmis={valmis} />}
      {sijainti && <Palautus n={tila.numero} tapa={h ? "palautus" : "sijainti"} otsikko={t("qr.kirjaa_sijaintiin")} nappi={t("pu.kirjaa")}
                             oletus={h ? tila.palautuksen_oletus : (l.sijainti_laji && l.sijainti_laji !== "henkilo"
                               ? { laji: l.sijainti_laji as KalustoSijaintilaji, kohde_id: l.sijainti_kohde_id ?? null, kohde: null, teksti: l.sijainti_teksti ?? null } : null)}
                             peru={() => setSijainti(false)} valmis={valmis} />}
      {Object.keys(tila.estot).length > 0 && (
        <ul className="qr__estot" data-qr-estot>
          {(["ota", "palauta", "muokkaa", "tarkastus"] as const).filter((t) => tila.estot[t]).map((t) => (
            <li key={t} data-qr-esto={t}>{tila.estot[t]}</li>))}
        </ul>)}
      {korvaus && <Korvaus n={tila.numero} peru={() => setKorvaus(false)} valmis={valmis} />}
    </div>
  );
}

/** Palautus: oletus «sinne mistä otettu», vaihdettavissa (Toni 26.9. kohta 2). */
function Palautus({ n, oletus, peru, valmis, tapa = "palautus", otsikko, nappi }: {
  n: number; oletus: Extract<QrTila, { tila: "sidottu" }>["palautuksen_oletus"]; peru: () => void; valmis: (v: string) => void;
  /** 76: sama lomake kirjaa haltijattoman laitteen sijaintiin (reitti sijainti) tai palauttaa haltijalta (reitti palautus). */
  tapa?: "palautus" | "sijainti"; otsikko?: string; nappi?: string;
}) {
  const otsikkoT = otsikko ?? t("qr.palauta"); const nappiT = nappi ?? t("qr.palauta");
  const [laji, setLaji] = useState<KalustoSijaintilaji | "">(oletus?.laji ?? "");
  const [kohdeId, setKohdeId] = useState<number | "">(oletus?.kohde_id ?? "");
  // T589 (arkkitehtuuri vaihe 1, kohdekoukku; Toni 16.10.2026 klo 03.45): oletus yläpalkin kohteesta (useKohde), vaihdettavissa.
  const { valittu: ylaKohde } = useKohde();
  useEffect(() => { if (oletus?.kohde_id == null && typeof ylaKohde === "number" && kohdeId === "") setKohdeId(ylaKohde); }, [ylaKohde]);
  const [teksti, setTeksti] = useState(oletus?.teksti ?? "");
  const [kohteet, setKohteet] = useState<Kohde[]>([]);
  const [virhe, setVirhe] = useState<string | null>(null);
  useEffect(() => { api.kohteet().then(setKohteet).catch(() => setKohteet([])); }, []);
  const laheta = async (e: React.FormEvent) => {
    e.preventDefault(); setVirhe(null);
    if (!laji) { setVirhe(tapa === "sijainti" ? t("qr.valitse_mihin_kirjataan") : t("qr.valitse_mihin_palautetaan")); return; }
    try {
      const syote = { sijainti_laji: laji, sijainti_kohde_id: laji === "kohde" && kohdeId !== "" ? Number(kohdeId) : null,
                      sijainti_teksti: laji !== "kohde" ? (teksti.trim() || null) : null };
      if (tapa === "sijainti") { await api.kalustoQrSijainti(n, syote); valmis(t("qr.kirjattu_sijaintiin")); }
      else { await api.kalustoQrPalauta(n, syote); valmis(t("qr.palautettu")); }
    } catch (x) { setVirhe(x instanceof ApiError ? virheTeksti(x.virhe, x.message) : t("qr.palautus_virhe")); }
  };
  return (
    <form className="qr__lomake" onSubmit={laheta} data-qr-lomake={tapa}>
      <p className="dim">{tapa === "sijainti" ? t("qr.oletus_lomakkeen")
        : oletus ? t("qr.oletus_mista_otettiin") : t("qr.oletus_ei_tiedossa")}</p>
      <label>{otsikkoT}
        <select value={laji} onChange={(e) => setLaji(e.target.value as KalustoSijaintilaji | "")} required>
          <option value="">{t("hl.valitse_viivat")}</option>
          {sijainnit().map((v) => <option key={v.arvo} value={v.arvo}>{v.nimi}</option>)}
        </select></label>
      {laji === "kohde" && (
        <label>{t("pu.kohde")}
          <select value={kohdeId} onChange={(e) => setKohdeId(e.target.value ? Number(e.target.value) : "")} required data-kohdekoukku="kalustoqr">
            <option value="">{t("hl.valitse_viivat")}</option>
            {kohteet.map((k) => <option key={k.id} value={k.id}>{k.tunnus} · {k.nimi}</option>)}
          </select></label>)}
      {laji && laji !== "kohde" && (
        <label>{laji === "auto" ? t("qr.rekisterinumero") : t("qr.sijainnin_nimi")}
          <input value={teksti} onChange={(e) => setTeksti(e.target.value)} required={laji === "auto"} maxLength={80}
                 placeholder={laji === "auto" ? t("qr.esim_rekisteri") : t("qr.esim_varasto")} /></label>)}
      {virhe && <p className="qr__esto" role="alert">{virhe}</p>}
      <div className="qr__toiminnot">
        <button className="nappi nappi--paa" type="submit">{nappiT}</button>
        <button className="nappi" type="button" onClick={peru}>{t("qr.peru")}</button>
      </div>
    </form>
  );
}

function Korvaus({ n, peru, valmis }: { n: number; peru: () => void; valmis: (v: string) => void }) {
  const [syy, setSyy] = useState<KalustoQrKorvausSyy>("tarra_vaurioitunut");
  const [peruste, setPeruste] = useState("");
  const [uusi, setUusi] = useState("");
  const [virhe, setVirhe] = useState<string | null>(null);
  const laheta = async (e: React.FormEvent) => {
    e.preventDefault(); setVirhe(null);
    try {
      const r = await api.kalustoQrKorvaa(n, { syy, peruste: peruste.trim(), uusi_numero: uusi.trim() ? Number(uusi) : null });
      valmis(r.uusi_numero ? t("qr.korvattu_uusi", { n, uusi: r.uusi_numero }) : t("qr.korvattu_ei_uutta", { n }));
    } catch (x) { setVirhe(x instanceof ApiError ? virheTeksti(x.virhe, x.message) : t("qr.korvaus_virhe")); }
  };
  return (
    <form className="qr__lomake" onSubmit={laheta} data-qr-lomake="korvaus">
      <p>{t("qr.korvaus_ohje")}</p>
      <label>{t("qr.syy")}
        <select value={syy} onChange={(e) => setSyy(e.target.value as KalustoQrKorvausSyy)}>
          {vaihtoehdot("kalusto_qr_korvaus_syy").map((v) => <option key={v.arvo} value={v.arvo}>{v.nimi}</option>)}
        </select></label>
      <label>{t("qr.peruste")}<input value={peruste} onChange={(e) => setPeruste(e.target.value)} required minLength={3} maxLength={500} /></label>
      <label>{t("qr.uuden_tarran_numero")}
        <input inputMode="numeric" pattern="[1-9][0-9]{0,8}" value={uusi} onChange={(e) => setUusi(e.target.value)} /></label>
      {virhe && <p className="qr__esto" role="alert">{virhe}</p>}
      <div className="qr__toiminnot">
        <button className="nappi nappi--paa" type="submit">{t("qr.korvaa_tarra", { n })}</button>
        <button className="nappi" type="button" onClick={peru}>{t("qr.peru")}</button>
      </div>
    </form>
  );
}

function Sitomaton({ n, tila, kayttaja, valmis, lisatty }: { lisatty?: () => void;
  n: number; tila: Extract<QrTila, { tila: "sitomaton" }>; kayttaja: Kayttaja; valmis: (v: string) => void;
}) {
  const [tapa, setTapa] = useState<"uusi" | "olemassa">("uusi");
  if (tila.esto) {
    return (
      <div data-qr-tila="sitomaton" data-sisalto="qr">
        <p className="qr__otsikko">{t("qr.sitomaton_otsikko")}</p>
        <p className="qr__esto" data-qr-esto>{tila.esto}</p>
      </div>
    );
  }
  return (
    <div data-qr-tila="sitomaton" data-sisalto="qr">
      <p className="qr__otsikko">{t("qr.sitomaton_otsikko")}</p>
      <p>{t("qr.mihin_liimasit")}</p>
      <div className="qr__valinta" role="group">
        <button className={"nappi" + (tapa === "uusi" ? " nappi--paa" : "")} aria-pressed={tapa === "uusi"}
                onClick={() => setTapa("uusi")} data-qr-toiminto="uusi">{t("qr.uusi_laite")}</button>
        <button className={"nappi" + (tapa === "olemassa" ? " nappi--paa" : "")} aria-pressed={tapa === "olemassa"}
                onClick={() => setTapa("olemassa")} data-qr-toiminto="olemassa">{t("qr.jo_rekisterissa")}</button>
      </div>
      {tapa === "uusi" ? <Lisays n={n} kayttaja={kayttaja} valmis={valmis} lisatty={lisatty} /> : <Sidonta n={n} valmis={valmis} />}
    </div>
  );
}

function Sidonta({ n, valmis }: { n: number; valmis: (v: string) => void }) {
  const [haku, setHaku] = useState("");
  const [tulos, setTulos] = useState<{ ehdokkaat: KalustoQrEhdokas[]; raja?: string } | null>(null);
  const [virhe, setVirhe] = useState<string | null>(null);
  useEffect(() => {
    const ajastin = setTimeout(() => {
      api.kalustoQrEhdokkaat(n, haku).then(setTulos).catch((e) => setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : t("pu.haku_virhe")));
    }, 250);
    return () => clearTimeout(ajastin);
  }, [n, haku]);
  const sido = async (e: KalustoQrEhdokas) => {
    setVirhe(null);
    try { await api.kalustoQrSido(n, e.id); valmis(t("qr.sidottu", { n, laite: `${e.tunnus} · ${e.nimi}` })); }
    catch (x) { setVirhe(x instanceof ApiError ? virheTeksti(x.virhe, x.message) : t("qr.sidonta_virhe")); }
  };
  return (
    <div className="qr__lomake" data-qr-lomake="sidonta">
      <label>{t("qr.hae")}
        <input value={haku} onChange={(e) => setHaku(e.target.value)} autoFocus /></label>
      {tulos?.raja && <p className="dim">{tulos.raja}</p>}
      {tulos && !tulos.raja && tulos.ehdokkaat.length === 0 && (
        <p className="dim">{t("qr.ei_tarratonta", { haku })}</p>)}
      <ul className="qr__ehdokkaat">
        {tulos?.ehdokkaat.map((e) => (
          <li key={e.id}>
            <button className="nappi" onClick={() => sido(e)} data-qr-ehdokas={e.id}>
              <strong>{e.tunnus}</strong> {e.nimi}
              <span className="dim"> {[e.tyyppi, e.sarjanumero && `s/n ${e.sarjanumero}`, sijaintiTeksti(e)].filter(Boolean).join(" · ")}</span>
            </button>
          </li>))}
      </ul>
      {virhe && <p className="qr__esto" role="alert">{virhe}</p>}
    </div>
  );
}

function Lisays({ n, kayttaja, valmis , lisatty }: { lisatty?: () => void;  n: number; kayttaja: Kayttaja; valmis: (v: string) => void }) {
  const [nimi, setNimi] = useState("");
  const [laji, setLaji] = useState<KalustoLaji>("tyokalu");
  const [tyyppi, setTyyppi] = useState("");
  const [sijaintiLaji, setSijaintiLaji] = useState<KalustoSijaintilaji | "">(kayttaja.kohteet.length ? "kohde" : "");
  const [kohdeId, setKohdeId] = useState<number | "">("");
  const [teksti, setTeksti] = useState("");
  const [kunto, setKunto] = useState("");
  const [valmistaja, setValmistaja] = useState("");
  const [malli, setMalli] = useState("");
  const [sarja, setSarja] = useState("");
  const [tyypit, setTyypit] = useState<KalustoTyyppi[]>([]);
  const [kohteet, setKohteet] = useState<Kohde[]>([]);
  const [virhe, setVirhe] = useState<string | null>(null);
  useEffect(() => {
    api.kalustoTyypit().then(setTyypit).catch(() => setTyypit([]));
    api.kohteet().then((k) => { setKohteet(k); if (k.length === 1) setKohdeId(k[0]!.id); }).catch(() => setKohteet([]));
  }, []);
  // T589 (arkkitehtuuri vaihe 1, kohdekoukku; Toni 16.10.2026 klo 03.45): oletus yläpalkin kohteesta (useKohde), vaihdettavissa.
  const { valittu: ylaKohde } = useKohde();
  useEffect(() => {
    if (typeof ylaKohde !== "number") return;
    if (kohdeId === "") setKohdeId(ylaKohde);
    if (sijaintiLaji === "") setSijaintiLaji("kohde");   // kohdekenttä näkyy vain lajilla «kohde»
  }, [ylaKohde]);
  const laheta = async (e: React.FormEvent) => {
    e.preventDefault(); setVirhe(null);
    const tyhja = (s: string) => s.trim() || null;
    try {
      const vastaus = await api.kalustoQrLisaa(n, {
        nimi: nimi.trim(), laji,
        tyyppi: laji === "henkilovaljaat" ? null : tyhja(tyyppi),
        sijainti_laji: sijaintiLaji || null,
        sijainti_kohde_id: sijaintiLaji === "kohde" && kohdeId !== "" ? Number(kohdeId) : null,
        sijainti_teksti: sijaintiLaji && sijaintiLaji !== "kohde" ? tyhja(teksti) : null,
        kunto: tyhja(kunto), valmistaja: tyhja(valmistaja), malli: tyhja(malli), sarjanumero: tyhja(sarja),
      });
      valmis(vastaus.hyvaksynta === "ehdotettu" ? t("qr.lisatty_ehdotus", { n }) : t("qr.lisatty", { n }));
      lisatty?.();
    } catch (x) { setVirhe(x instanceof ApiError ? virheTeksti(x.virhe, x.message) : t("qr.lisays_virhe")); }
  };
  return (
    <form className="qr__lomake" onSubmit={laheta} data-qr-lomake="lisays">
      {/* 5.10.2026 (protot K-1): «Nimi» luettiin kentällä omaksi nimeksi — ensimmäinen laite sai lisääjän nimen. */}
      <label>{t("qr.laitteen_nimi")}<input value={nimi} onChange={(e) => setNimi(e.target.value)} required maxLength={120} autoFocus
                                placeholder={t("qr.laitteen_nimi_esim")} /></label>
      <label>{t("qr.laji")}
        <select value={laji} onChange={(e) => { setLaji(e.target.value as KalustoLaji); setTyyppi(""); }}>
          {vaihtoehdot("kalusto_laji").map((v) => <option key={v.arvo} value={v.arvo}>{v.nimi}</option>)}
        </select></label>
      {laji !== "henkilovaljaat" && (
        <label>{t("pu.tyyppi")}
          <select value={tyyppi} onChange={(e) => setTyyppi(e.target.value)}>
            <option value="">{t("qr.ei_valittu")}</option>
            {tyypit.filter((t) => t.laji === laji && t.aktiivinen).map((t) => <option key={t.id} value={t.nimi}>{t.nimi}</option>)}
          </select></label>)}
      <label>{t("qr.sijainti")}
        <select value={sijaintiLaji} onChange={(e) => setSijaintiLaji(e.target.value as KalustoSijaintilaji | "")}>
          <option value="">{t("qr.ei_tiedossa_viivat")}</option>
          {sijainnit().map((v) => <option key={v.arvo} value={v.arvo}>{v.nimi}</option>)}
        </select></label>
      {sijaintiLaji === "kohde" && (
        <label>{t("pu.kohde")}
          <select value={kohdeId} onChange={(e) => setKohdeId(e.target.value ? Number(e.target.value) : "")} required data-kohdekoukku="kalustoqr">
            <option value="">{t("hl.valitse_viivat")}</option>
            {kohteet.map((k) => <option key={k.id} value={k.id}>{k.tunnus} · {k.nimi}</option>)}
          </select></label>)}
      {sijaintiLaji && sijaintiLaji !== "kohde" && (
        <label>{sijaintiLaji === "auto" ? t("qr.rekisterinumero") : t("qr.sijainnin_nimi")}
          <input value={teksti} onChange={(e) => setTeksti(e.target.value)} required={sijaintiLaji === "auto"} maxLength={80}
                 placeholder={sijaintiLaji === "auto" ? t("qr.esim_rekisteri") : t("qr.esim_varasto")} /></label>)}
      <label>{t("qr.kunto")}
        <select value={kunto} onChange={(e) => setKunto(e.target.value)}>
          <option value="">{t("qr.ei_kirjattu_viivat")}</option>
          {vaihtoehdot("kalusto_kunto").map((v) => <option key={v.arvo} value={v.arvo}>{v.nimi}</option>)}
        </select></label>
      <label>{t("qr.valmistaja")}<input value={valmistaja} onChange={(e) => setValmistaja(e.target.value)} maxLength={80} /></label>
      <label>{t("qr.malli")}<input value={malli} onChange={(e) => setMalli(e.target.value)} maxLength={80} /></label>
      <label>{t("qr.sarjanumero")}<input value={sarja} onChange={(e) => setSarja(e.target.value)} maxLength={80} /></label>
      {/* 176: laitteen tunnus annetaan tallennettaessa seuraavana numerona. Se ei ole tarranumero: tarra
          vaihtuu (uudelleensidonta poistaa vanhan numeron), laite ei. */}
      <p className="dim" data-tunnus-automaattinen="">{t("qr.tunnus_automaattinen", { n })}</p>
      {virhe && <p className="qr__esto" role="alert">{virhe}</p>}
      <button className="nappi nappi--paa" type="submit">{t("qr.lisaa_ja_sido", { n })}</button>
    </form>
  );
}
